import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line } from 'recharts';
import { Info } from 'lucide-react';

const SynaptopathyHistory = () => {
  const [newDiseasesData, setNewDiseasesData] = useState([]);
  const [accumulatedDiseasesData, setAccumulatedDiseasesData] = useState([]);
  const [geneLocalizationsData, setGeneLocalizationsData] = useState([]);
  const [accumulatedLocalizationsData, setAccumulatedLocalizationsData] = useState([]);
  const [activeChart, setActiveChart] = useState('accumulated');
  const [activeLocalization, setActiveLocalization] = useState('all');
  const [localizationsList, setLocalizationsList] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Define colors for multiple lines
  const COLORS = ['#8884d8', '#82ca9d', '#ffc658', '#ff8042', '#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#a4de6c', '#d0ed57'];

  // Helper function to format localization names
  const formatLocalizationName = (loc) => {
    // Format specific localization names
    if (loc === "Synaptic_Vesicle") {
      return "Synaptic Vesicle";
    }
    // Add more formatting rules as needed

    // Replace underscores with spaces for other localizations
    return loc.replace(/_/g, ' ');
  };

  useEffect(() => {
    document.title = 'Synaptopathy History'; // Set the page title
  }, []);

  useEffect(() => {
    const fetchAllData = async () => {
      try {
        setIsLoading(true);

        // Fetch accumulated diseases data
        const accumulatedDiseasesResponse = await fetch('/api/stats/accumulated-diseases');
        const accumulatedDiseasesResult = await accumulatedDiseasesResponse.json();

        if (accumulatedDiseasesResult.success) {
          setAccumulatedDiseasesData(accumulatedDiseasesResult.data);
        } else {
          setError(accumulatedDiseasesResult.message || 'Failed to load accumulated diseases statistics');
        }

        // Fetch new diseases data
        const newDiseasesResponse = await fetch('/api/stats/new-diseases');
        const newDiseasesResult = await newDiseasesResponse.json();

        if (newDiseasesResult.success) {
          setNewDiseasesData(newDiseasesResult.data);
        } else {
          setError(prevError =>
            prevError
              ? `${prevError}. ${newDiseasesResult.message || 'Failed to load new diseases statistics'}`
              : newDiseasesResult.message
          );
        }

        // Fetch accumulated gene localizations data (new API)
        const accumulatedLocalizationsResponse = await fetch('/api/stats/accumulated-gene-localizations');
        const accumulatedLocalizationsResult = await accumulatedLocalizationsResponse.json();

        if (accumulatedLocalizationsResult.success) {
          setAccumulatedLocalizationsData(accumulatedLocalizationsResult.data);

          // Extract localization names for the toggle options
          if (accumulatedLocalizationsResult.localizations) {
            setLocalizationsList(accumulatedLocalizationsResult.localizations);
          } else if (accumulatedLocalizationsResult.data.length > 0) {
            // Extract localization names from the first data point (excluding 'year' and 'total_accumulated')
            const localizations = Object.keys(accumulatedLocalizationsResult.data[0])
              .filter(key => key !== 'year' && key !== 'total_accumulated');
            setLocalizationsList(localizations);
          }
        } else {
          setError(prevError =>
            prevError
              ? `${prevError}. ${accumulatedLocalizationsResult.message || 'Failed to load accumulated gene localizations statistics'}`
              : accumulatedLocalizationsResult.message
          );
        }

      } catch (err) {
        setError('Error fetching data: ' + err.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAllData();
  }, []);

  const renderChart = (data, dataKey, title, description, chartType = 'bar') => {
    if (isLoading) {
      return (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        </div>
      );
    }

    if (error) {
      return (
        <div className="bg-red-50 p-4 rounded-md">
          <div className="flex">
            <div className="flex-shrink-0">
              <Info className="h-5 w-5 text-red-400" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error loading data</h3>
              <div className="mt-2 text-sm text-red-700">{error}</div>
            </div>
          </div>
        </div>
      );
    }

    if (!data.length) {
      return (
        <div className="text-center py-10 text-gray-500">
          No disease statistics available
        </div>
      );
    }

    return (
      <div>
        <h2 className="text-xl font-semibold text-gray-800 mb-4">{title}</h2>
        <p className="text-gray-600 mb-6">
          {description}
        </p>
        <ResponsiveContainer width="100%" height={400}>
          {chartType === 'bar' ? (
            <BarChart
              data={data}
              margin={{
                top: 20,
                right: 30,
                left: 20,
                bottom: 60,
              }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="year"
                angle={-45}
                textAnchor="end"
                height={60}
                interval={0}
              />
              <YAxis
                label={{
                  value: 'Number of Diseases',
                  angle: -90,
                  position: 'insideLeft',
                  style: { textAnchor: 'middle' }
                }}
              />
              <Tooltip
                formatter={(value) => [`${value} diseases`, 'Count']}
                labelFormatter={(year) => `Year: ${year}`}
              />
              <Legend />
              <Bar
                dataKey={dataKey}
                name="Diseases"
                fill="#4299e1"
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          ) : (
            <LineChart
              data={data}
              margin={{
                top: 20,
                right: 30,
                left: 20,
                bottom: 60,
              }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="year"
                angle={-45}
                textAnchor="end"
                height={60}
                interval={0}
              />
              <YAxis
                label={{
                  value: 'Total Number of Diseases',
                  angle: -90,
                  position: 'insideLeft',
                  style: { textAnchor: 'middle' }
                }}
              />
              <Tooltip
                formatter={(value) => [`${value} diseases`, 'Total Count']}
                labelFormatter={(year) => `Year: ${year}`}
              />
              <Legend />
              <Line
                type="monotone"
                dataKey={dataKey}
                name="Cumulative Diseases"
                stroke="#4299e1"
                strokeWidth={2}
                dot={{ r: 3 }}
                activeDot={{ r: 6 }}
              />
            </LineChart>
          )}
        </ResponsiveContainer>
      </div>
    );
  };


  const renderAccumulatedLocalizationsChart = () => {
    if (isLoading) {
      return (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        </div>
      );
    }

    if (error) {
      return (
        <div className="bg-red-50 p-4 rounded-md">
          <div className="flex">
            <div className="flex-shrink-0">
              <Info className="h-5 w-5 text-red-400" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error loading data</h3>
              <div className="mt-2 text-sm text-red-700">{error}</div>
            </div>
          </div>
        </div>
      );
    }

    if (!accumulatedLocalizationsData.length) {
      return (
        <div className="text-center py-10 text-gray-500">
          No accumulated gene localization data available
        </div>
      );
    }

    return (
      <div>
        <h2 className="text-xl font-semibold text-gray-800 mb-4">Synapse Protein Localization Trend</h2>
        <p className="text-gray-600 mb-4">
          This chart displays the accumulated identification of subcellular localizations for genes over time,
          showing the cumulative growth in our understanding of synaptic protein localization.
          It is based on the same subset of 1,236 synaptic genes from the 3,437-gene,
          all of which are OMIM-annotated with at least one disease phenotype.
        </p>

        {/* Localization toggle controls */}
        <div className="mb-6 flex flex-wrap gap-2">
          <button
            onClick={() => setActiveLocalization('all')}
            className={`px-3 py-1 text-sm rounded ${activeLocalization === 'all'
              ? 'bg-blue-500 text-white'
              : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
              }`}
          >
            All Localizations
          </button>
          <button
            onClick={() => setActiveLocalization('total')}
            className={`px-3 py-1 text-sm rounded ${activeLocalization === 'total'
              ? 'bg-blue-500 text-white'
              : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
              }`}
          >
            Total
          </button>
          {[...localizationsList].sort().map((loc) => {
            // Use the formatting function for display names
            const displayName = formatLocalizationName(loc);

            return (
              <button
                key={loc}
                onClick={() => setActiveLocalization(loc)}
                className={`px-3 py-1 text-sm rounded ${activeLocalization === loc
                  ? 'bg-blue-500 text-white'
                  : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
                  }`}
              >
                {displayName}
              </button>
            );
          })}
        </div>

        <ResponsiveContainer width="100%" height={400}>
          <LineChart
            data={accumulatedLocalizationsData}
            margin={{
              top: 20,
              right: 30,
              left: 20,
              bottom: 60,
            }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="year"
              angle={-45}
              textAnchor="end"
              height={60}
              interval={0}
            />
            <YAxis
              label={{
                value: 'Synaptopathic Genes',
                angle: -90,
                position: 'insideLeft',
                style: { textAnchor: 'middle' }
              }}
            />
            <Tooltip
              formatter={(value, name) => {
                // Format the name if it's a localization
                if (localizationsList.includes(name)) {
                  return [`${value} genes`, formatLocalizationName(name)];
                }
                // For "total_accumulated" or other non-localization keys
                if (name === "total_accumulated") {
                  return [`${value} genes`, "Total Accumulated"];
                }
                return [`${value} genes`, name];
              }}
              labelFormatter={(year) => `Year: ${year}`}
            />
            <Legend />

            {/* Display based on selected localization toggle */}
            {activeLocalization === 'all' &&
              localizationsList.map((loc, index) => (
                <Line
                  key={loc}
                  type="monotone"
                  dataKey={loc}
                  name={formatLocalizationName(loc)}
                  stroke={COLORS[index % COLORS.length]}
                  strokeWidth={2}
                  dot={{ r: 2 }}
                  activeDot={{ r: 5 }}
                />
              ))
            }
            {activeLocalization === 'total' &&
              <Line
                type="monotone"
                dataKey="total_accumulated"
                name="Total Accumulated"
                stroke="#4299e1"
                strokeWidth={3}
                dot={{ r: 3 }}
                activeDot={{ r: 6 }}
              />
            }
            {activeLocalization !== 'all' && activeLocalization !== 'total' &&
              <Line
                type="monotone"
                dataKey={activeLocalization}
                name={formatLocalizationName(activeLocalization)}
                stroke={COLORS[0]}
                strokeWidth={3}
                dot={{ r: 3 }}
                activeDot={{ r: 6 }}
              />
            }
          </LineChart>
        </ResponsiveContainer>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Synaptopathy History</h1>
          <p className="mt-2 text-gray-600">Visualizing disease trends and gene localizations in Synaptopathy</p>
        </div>

        <div className="mb-6 flex flex-wrap gap-2">
          <button
            onClick={() => setActiveChart('accumulated')}
            className={`px-4 py-2 rounded ${activeChart === 'accumulated'
              ? 'bg-blue-500 text-white'
              : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
              }`}
          >
            Disease Trend
          </button>
          <button
            onClick={() => setActiveChart('new')}
            className={`px-4 py-2 rounded ${activeChart === 'new'
              ? 'bg-blue-500 text-white'
              : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
              }`}
          >
            Disease Discovery Rate
          </button>
          <button
            onClick={() => setActiveChart('accumulated-localizations')}
            className={`px-4 py-2 rounded ${activeChart === 'accumulated-localizations'
              ? 'bg-blue-500 text-white'
              : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
              }`}
          >
            Synaptopathy Protein History
          </button>
        </div>

        <div className="bg-white shadow rounded-lg p-6">
          {activeChart === 'accumulated' && renderChart(
            accumulatedDiseasesData,
            'accumulated_disease_count',
            'Disease Trend Over Time',
            'This chart shows the total accumulated number of known diseases (nervous & non nervous system) over time, representing the growing catalogue of identified conditions for the 1,236 OMIM-annotated synaptic genes in our 3,437-gene consensus set.',
            'line'
          )}

          {activeChart === 'new' && renderChart(
            newDiseasesData,
            'new_disease_count',
            'Disease Discovery Rate',
            'This chart shows the annual discovery of new diseases (nervous & non nervous system) for the 1,236 OMIM-annotated synaptic genes from our 3,437-gene consensus set.'
          )}


          {activeChart === 'accumulated-localizations' && renderAccumulatedLocalizationsChart()}
        </div>

        <div className="mt-8 bg-white shadow rounded-lg p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">About these data</h2>
          {activeChart === 'accumulated' && (
            <>
              <p className="text-gray-600">
                The "Disease Trend" chart displays the total running count of known diseases linked to synaptic proteins over time, showing how our knowledge of diseases has accumulated year by year. Each data point represents the total number of diseases identified up to that specific year.
              </p>
              <p className="text-gray-600 mt-4">
                This visualization is based on the same dataset as the Disease Discovery Rate chart, focusing on the 1,236 OMIM-annotated synaptic genes from our 3,437-gene consensus set. Each of these genes has at least one documented disease phenotype, making them our primary “disease subset.”
              </p>
            </>
          )}

          {activeChart === 'new' && (
            <>
              <p className="text-gray-600">
                The "Disease Discovery Rate" chart represents the count of new diseases discovered each year based on their
                first mention in research literature, focusing on a consensus set of approximately 1236 genes.
              </p>
              <p className="text-gray-600 mt-4">
                The data is sourced from a separate table containing curated gene-disease discovery years
                for the same subset of 1,236 OMIM-annotated synaptic genes out of our 3,437-gene. While not exhaustive, this subset is considered representative
                of the broader database of synaptopathic genes.
              </p>
            </>
          )}

          {activeChart === 'accumulated-localizations' && (
            <>
              <p className="text-gray-600">
                The "Synaptopathy Protein History" chart shows the cumulative growth in identified subcellular localizations of proteins over time.
                Each line represents either all localizations together, a single localization type, or the total across all localizations.
              </p>
              <p className="text-gray-600 mt-4">
                This accumulated view illustrates how our understanding of synaptic protein localizations
                has grown over time, with each year building upon previous discoveries.
                Like the disease charts, this visualization is also based on the 1,236 OMIM-annotated genes
                in our 3,440-gene, focusing on those with documented disease associations.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SynaptopathyHistory;